import { createContext, useContext, useEffect, useState, useCallback, type ReactNode } from 'react'

// Mode de thème: light, dark, ou system (suit les préférences OS)
export type ThemeMode = 'light' | 'dark' | 'system'

// Thème effectif appliqué (light ou dark)
type EffectiveTheme = 'light' | 'dark'

interface ThemeContextType {
  themeMode: ThemeMode
  effectiveTheme: EffectiveTheme
  setThemeMode: (mode: ThemeMode) => void
  isDark: boolean
  // Legacy support
  theme: EffectiveTheme
  toggleTheme: () => void
}

const ThemeContext = createContext<ThemeContextType | undefined>(undefined)

const STORAGE_KEY = 'betterclose-theme-mode'

// Détecter la préférence système
function getSystemTheme(): EffectiveTheme {
  if (typeof window === 'undefined') return 'dark'
  return window.matchMedia('(prefers-color-scheme: dark)').matches ? 'dark' : 'light'
}

// Calculer le thème effectif
function getEffectiveTheme(mode: ThemeMode): EffectiveTheme {
  if (mode === 'system') {
    return getSystemTheme()
  }
  return mode
}

export function ThemeProvider({ children }: { children: ReactNode }) {
  // Initialiser depuis localStorage ou dark par défaut
  const [themeMode, setThemeModeState] = useState<ThemeMode>(() => {
    if (typeof window !== 'undefined') {
      const saved = localStorage.getItem(STORAGE_KEY) as ThemeMode | null
      if (saved && ['light', 'dark', 'system'].includes(saved)) {
        return saved
      }
    }
    return 'dark' // Dark par défaut
  })

  // Thème effectif (light ou dark)
  const [effectiveTheme, setEffectiveTheme] = useState<EffectiveTheme>(() =>
    getEffectiveTheme(themeMode)
  )

  // Appliquer le thème au DOM.
  //
  // Also sync `document.body.style` so the hardcoded-dark body inline
  // style from index.html doesn't override our light-mode CSS rules via
  // spec-max specificity. Mustapha 2026-04-22 QA: without this, flipping
  // to light mode from the Navbar toggle left the body's inline
  // `color:#fafafa` + `background:#0f0f10` in place and the whole page
  // stayed dark under a thin coat of light tokens on individual elements.
  const applyTheme = useCallback((theme: EffectiveTheme) => {
    const root = document.documentElement
    if (theme === 'dark') {
      root.classList.add('dark')
      root.classList.remove('light')
      if (document.body) {
        document.body.style.background = '#0f0f10'
        document.body.style.color = '#fafafa'
      }
    } else {
      root.classList.add('light')
      root.classList.remove('dark')
      if (document.body) {
        document.body.style.background = '#fafafa'
        document.body.style.color = '#09090b'
      }
    }
  }, [])

  // Changer le mode de thème
  const setThemeMode = useCallback((mode: ThemeMode) => {
    setThemeModeState(mode)
    localStorage.setItem(STORAGE_KEY, mode)

    const effective = getEffectiveTheme(mode)
    setEffectiveTheme(effective)
    applyTheme(effective)
  }, [applyTheme])

  // Legacy toggle (cycle through: dark -> light -> system -> dark)
  const toggleTheme = useCallback(() => {
    const nextMode: Record<ThemeMode, ThemeMode> = {
      dark: 'light',
      light: 'system',
      system: 'dark'
    }
    setThemeMode(nextMode[themeMode])
  }, [themeMode, setThemeMode])

  // Écouter les changements de préférence système
  useEffect(() => {
    const mediaQuery = window.matchMedia('(prefers-color-scheme: dark)')

    const handleChange = () => {
      if (themeMode === 'system') {
        const newEffective = getSystemTheme()
        setEffectiveTheme(newEffective)
        applyTheme(newEffective)
      }
    }

    // Appliquer le thème initial
    applyTheme(effectiveTheme)

    // Écouter les changements
    mediaQuery.addEventListener('change', handleChange)
    return () => mediaQuery.removeEventListener('change', handleChange)
  }, [themeMode, effectiveTheme, applyTheme])

  // Appliquer le thème au montage
  useEffect(() => {
    applyTheme(effectiveTheme)
  }, [effectiveTheme, applyTheme])

  return (
    <ThemeContext.Provider
      value={{
        themeMode,
        effectiveTheme,
        setThemeMode,
        isDark: effectiveTheme === 'dark',
        // Legacy support
        theme: effectiveTheme,
        toggleTheme
      }}
    >
      {children}
    </ThemeContext.Provider>
  )
}

// eslint-disable-next-line react-refresh/only-export-components
export function useTheme() {
  const context = useContext(ThemeContext)
  if (context === undefined) {
    throw new Error('useTheme must be used within a ThemeProvider')
  }
  return context
}
